import { Delete, Download } from '@mui/icons-material'
import { Box, Button, IconButton, SxProps } from '@mui/material'
import React, { useEffect, useState } from 'react'
import ImageGallery from 'react-image-gallery'
import 'react-image-gallery/styles/css/image-gallery.css'

type ImageGalleryProps = {
	blobs: Blob[]
	setBlobs?: React.Dispatch<React.SetStateAction<Blob[]>>
	handleDelete?: (index: number) => void
	readOnly?: boolean
	sx?: SxProps
}

export function ImageSlider({ blobs, setBlobs, handleDelete, readOnly, sx }: ImageGalleryProps) {
	const [imageData, setImageData] = useState<{ original: string; thumbnail: string }[]>([])
	const [currentIndex, setCurrentIndex] = useState(0)
	const [isFullscreen, setIsFullscreen] = useState(false)
	useEffect(() => {
		if (blobs.length == 0) setImageData([])
		const convertBlobsToBase64 = async () => {
			const base64Array = await Promise.all(blobs.map(blobToBase64))
			setImageData(base64Array.map((base) => ({ original: base, thumbnail: base })))
		}

		if (blobs.length > 0) {
			convertBlobsToBase64()
		}
	}, [blobs])
	const deleteImage = () => {
		if (handleDelete) {
			handleDelete(currentIndex)
		} else {
			setBlobs?.((prevImages) => prevImages.filter((_, index) => index !== currentIndex))
		}

		// Reset index if the last image was deleted
		setCurrentIndex((prevIndex) => (prevIndex > 0 ? prevIndex - 1 : 0))
	}
	const blobToBase64 = (blob: Blob): Promise<string> => {
		return new Promise((resolve, reject) => {
			const reader = new FileReader()
			reader.readAsDataURL(blob)
			reader.onloadend = () => {
				if (typeof reader.result === 'string') {
					resolve(reader.result)
				} else {
					reject(new Error('Failed to convert Blob to Base64'))
				}
			}
			reader.onerror = reject
		})
	}
	function downloadImage(imageSrc: string): void {
		const link = document.createElement('a')
		link.href = imageSrc
		link.download = `image-${currentIndex + 1}.jpg` // Name file uniquely
		document.body.appendChild(link)
		link.click()
		document.body.removeChild(link)
	}

	return (
		<Box
			sx={
				sx ?? {
					width: '100%',
					maxWidth: 700,
					margin: '0 auto',
					'& .image-gallery': {
						width: '100%',
					},
					// Preview mode: keep a stable container height and scale image down to fit.
					'& .image-gallery-content:not(.fullscreen) .image-gallery-slide': {
						height: 'clamp(220px, 45vh, 420px)',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						backgroundColor: '#000',
					},
					'& .image-gallery-content:not(.fullscreen) .image-gallery-slide .image-gallery-image': {
						width: '100%',
						height: '100%',
						objectFit: 'contain',
					},
					'& .image-gallery-thumbnails': {
						paddingTop: 8,
					},
					'& .image-gallery-thumbnail img': {
						objectFit: 'cover',
					},
					// Fullscreen mode: allow image to use the whole viewport.
					'& .image-gallery.fullscreen-modal .image-gallery-content': {
						top: 0,
						transform: 'none',
						height: '100%',
					},
					'& .image-gallery.fullscreen-modal .image-gallery-slide': {
						height: '100vh',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						backgroundColor: '#000',
					},
					'& .image-gallery.fullscreen-modal .image-gallery-slide .image-gallery-image': {
						width: '100vw',
						height: '100vh',
						objectFit: 'contain',
					},
				}
			}
		>
			<ImageGallery
				onScreenChange={(fullscreen) => setIsFullscreen(fullscreen)}
				items={imageData}
				showThumbnails={!isFullscreen}
				showPlayButton={false}
				showFullscreenButton={true}
				showNav={true}
				onSlide={(index) => setCurrentIndex(index)}
				renderCustomControls={() => (
					<>
						<Button
							variant='contained'
							startIcon={<Download />}
							onClick={() => downloadImage(imageData[currentIndex].original)}
							sx={{
								position: 'absolute',
								top: 10,
								right: 30,
								backgroundColor: 'rgba(0, 0, 0, 0.5)',
								color: 'white',
								'&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.7)' },
								zIndex: 999,
							}}
						>
							Download
						</Button>
						{!readOnly && (
							<IconButton
								onClick={deleteImage}
								sx={{
									backgroundColor: 'rgba(255, 0, 0, 0.5)',
									color: 'white',
									'&:hover': { backgroundColor: 'rgba(200, 0, 0, 0.9)' },
									position: 'absolute',
									top: 10,
									left: 30,
									zIndex: 999,
									borderRadius: '5px',
								}}
							>
								<Delete />
							</IconButton>
						)}
					</>
				)}
			/>
		</Box>
	)
}
